<template>
  <div
    v-loading="isInitialLoading"
    class="tw-h-full tw-flex tw-flex-col"
  >
    <div class="tw-flex tw-justify-between tw-items-center">
      <router-link
        v-if="$isInRole(['FOREIGNER'])"
        to="/"
        class="tw-w-1/2 md:tw-w-1/3"
      >
        <i-button-icon-text
          icon="icon-angle-left"
        >
          {{ $t('foreigner.permits.back_to_dashboard') }}
        </i-button-icon-text>
      </router-link>
      <h1 class="tw-w-1/2 md:tw-w-1/3 tw-text-center">
        {{ $t('common.faq.title') }}
      </h1>
      <DebouncedSearch
        :query="searchQuery"
        :search="search"
        :placeholder="$t('common.actions.search')"
        class="tw-w-1/2 md:tw-w-1/3 tw-h-10"
      />
    </div>

    <div
      v-if="noFaqSections"
      class="tw-h-full tw-flex tw-items-center tw-justify-center"
    >
      {{ $t('common.search.empty') }}
    </div>

    <div
      v-else
      class="tw-flex-grow tw-overflow-hidden tw-flex"
    >
      <el-tabs
        :model-value="tab"
        tab-position="left"
        class="tw-mt-4"
        type="border-card"
        @tab-click="handleTabChange"
      >
        <template
          v-for="faqSection in faqSections"
        >
          <el-tab-pane
            v-if="faqSection.queries.length > 0 && getTextInCurrentLanguage(faqSection.name_en, faqSection.name_ru).length < 26"
            :key="faqSection.id"
            :name="faqSection.id.toString()"
          >
            <template #label>
              <el-badge
                :value="faqSection.queries.length"
                :hidden="filters.search.length < 3"
                class="faq-badge"
              >
                <span>
                  {{ getTextInCurrentLanguage(faqSection.name_en, faqSection.name_ru) }}
                </span>
              </el-badge>
            </template>
            <div class="tw-flex tw-flex-col tw-gap-4 tw-w-full">
              <i-collapse-item
                v-for="(question, index) in faqSection.queries"
                :key="question.id + currentFaqSection.id + faqSections.length"
                :title="getTextInCurrentLanguage(question.name_en, question.name_ru, `Question #${index + 1}`)"
              >
                <template #content>
                  <div class="tw-px-6">
                    <p class="tw-leading-5 tw-tracking-normal tw-whitespace-pre-line">
                      {{ getTextInCurrentLanguage(question.answer_en, question.answer_ru, `Answer #${index + 1}`) }}
                    </p>
                  </div>
                </template>
              </i-collapse-item>
            </div>
          </el-tab-pane>
          <el-tab-pane
            v-else-if="faqSection.queries.length > 0"
            :key="faqSection.id + 'else'"
            :name="faqSection.id.toString()"
          >
            <template #label>
              <el-tooltip
                :content="getTextInCurrentLanguage(faqSection.name_en, faqSection.name_ru)"
              >
                <el-badge
                  :value="faqSection.queries.length"
                  :hidden="filters.search.length < 3"
                  class="faq-badge"
                >
                  <span>
                    {{ getTextInCurrentLanguage(faqSection.name_en, faqSection.name_ru).substring(0, 24) }}...
                  </span>
                </el-badge>
              </el-tooltip>
            </template>
            <div class="tw-flex tw-flex-col tw-gap-4 tw-w-full">
              <i-collapse-item
                v-for="(question, index) in faqSection.queries"
                :key="question.id + currentFaqSection.id + faqSections.length"
                :title="getTextInCurrentLanguage(question.name_en, question.name_ru, `Question #${index + 1}`)"
                :class="[
                  question.hasError ? 'tw-border tw-border-primary' : '',
                ]"
              >
                <template #content>
                  <div class="tw-px-6">
                    <p class="tw-whitespace-pre-line">
                      {{ getTextInCurrentLanguage(question.answer_en, question.answer_ru, `Answer #${index + 1}`) }}
                    </p>
                  </div>
                </template>
              </i-collapse-item>
            </div>
          </el-tab-pane>
        </template>
      </el-tabs>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import DebouncedSearch from '@/components/shared/DebouncedSearch.vue';

export default {
  name: 'FAQsV2',
  components: { DebouncedSearch },
  data() {
    return {
      tab: '',
      isInitialLoading: false,
      searchQuery: '',
    };
  },
  computed: {
    ...mapState({
      me: (state) => state.v2.shared.profile.me,
      isLoading: (state) => state.v2.shared.faq.isLoading,
      faqSections: (state) => state.v2.shared.faq.faqSections,
      filters: (state) => state.v2.shared.faq.filters,
    }),
    ...mapGetters({
      noFaqSections: 'v2/shared/faq/ifEmpty',
    }),
    currentFaqSection() {
      return this.faqSections.find((item) => item.id.toString() === this.tab);
    },
  },
  watch: {
    faqSections() {
      if (this.faqSections.length > 0) {
        this.tab = this.faqSections[0]?.id.toString();
      }
    },
    filters: {
      handler() {
        this.$store.dispatch('v2/shared/faq/load');
      },
      deep: true,
    },
  },
  async mounted() {
    this.isInitialLoading = true;
    await this.$store.dispatch('v2/shared/faq/load');
    this.isInitialLoading = false;
  },
  methods: {
    getTextInCurrentLanguage(enValue, ruValue, defaultValue = '') {
      return (this.me.interface_language === 2 ? ruValue : enValue) ?? defaultValue;
    },
    handleTabChange(tab) {
      this.tab = tab.props.name;
    },
    search(query) {
      this.$store.commit('v2/shared/faq/updateFilter', { field: 'search', payload: query });
    },
  },
};
</script>
